import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { TuiButton, TuiDialogService, TuiNotificationService } from '@taiga-ui/core';
import { tuiDialog } from '@taiga-ui/core';
import { TuiDialogContext } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { RegisterRequest } from '../../models/register.model';
import { AuthService } from '../../services/auth.service';
import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { TurnstileWidgetComponent } from '../../../../shared/components/turnstile-widget/turnstile-widget.component';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-register-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TuiButton, TurnstileWidgetComponent],
  templateUrl: './register-dialog.component.html',
  styleUrl: './register-dialog.component.less',
})
export class RegisterDialogComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private alerts = inject(TuiNotificationService);
  private readonly dialogs = inject(TuiDialogService);
  private readonly context = inject<TuiDialogContext<void, void>>(POLYMORPHEUS_CONTEXT);

  private readonly loginDialog = tuiDialog(LoginDialogComponent, {
    size: 'm',
    // Non-dismissible: the Cap widget resizes on click, so an overlay/Esc
    // dismiss can fire mid-interaction and drop the form. Close via the X.
    dismissible: false,
    label: 'Sign In',
  });

  private readonly turnstileWidget = viewChild<TurnstileWidgetComponent>('turnstileWidget');

  registerForm = this.fb.group(
    {
      username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirm_password: ['', [Validators.required]],
    },
    { validators: this.passwordMatchValidator }
  );

  loading = signal(false);
  turnstileToken = signal<string | null>(null);

  /** Site key configured locally AND backend admin toggle is on. */
  readonly turnstileEnabled = computed(
    () => !!environment.turnstileSiteKey && this.authService.captchaEnabled(),
  );

  get usernameControl() {
    return this.registerForm.get('username');
  }

  get emailControl() {
    return this.registerForm.get('email');
  }

  get passwordControl() {
    return this.registerForm.get('password');
  }

  get confirmPasswordControl() {
    return this.registerForm.get('confirm_password');
  }

  /** Whether the submit button should be disabled. */
  get submitDisabled(): boolean {
    if (this.registerForm.invalid || this.loading()) {
      return true;
    }
    // When Turnstile is enabled, require a valid token
    if (this.turnstileEnabled() && !this.turnstileToken()) {
      return true;
    }
    return false;
  }

  get submitAppearance(): 'positive' | 'primary' {
    return this.submitDisabled ? 'primary' : 'positive';
  }

  passwordMatchValidator(form: AbstractControl): ValidationErrors | null {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirm_password')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  onTurnstileToken(token: string): void {
    this.turnstileToken.set(token || null);
  }

  onTurnstileExpired(): void {
    this.turnstileToken.set(null);
  }

  onTurnstileError(errorCode: string): void {
    console.error('[RegisterDialog] Turnstile error:', errorCode);
    this.turnstileToken.set(null);
  }

  onSubmit(): void {
    if (this.registerForm.valid) {
      this.loading.set(true);
      const userData: RegisterRequest = {
        username: this.registerForm.value.username || '',
        email: this.registerForm.value.email || '',
        password: this.registerForm.value.password || '',
      };

      // Include Turnstile token if enabled and available
      if (this.turnstileEnabled() && this.turnstileToken()) {
        userData.turnstile_token = this.turnstileToken();
      }

      this.authService
        .register(userData)
        .pipe(
          tap(() => {
            this.loading.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.context.completeWith();
            this.alerts
              .open('Account created successfully! Please sign in.', {
                label: 'Success',
                appearance: 'positive',
                autoClose: 3000,
              })
              .subscribe();
          },
          error: (error) => {
            this.loading.set(false);
            // Reset Turnstile widget on error (tokens are single-use)
            this.turnstileToken.set(null);
            this.turnstileWidget()?.reset();

            this.alerts
              .open(`Registration failed: ${error.error?.detail || error.message || 'Unknown error'}`, {
                label: 'Error',
                appearance: 'negative',
                autoClose: 0,
              })
              .subscribe();
          },
        });
    }
  }

  openLoginDialog(): void {
    this.context.completeWith();
    this.loginDialog().subscribe();
  }
}
